import { useState, useEffect, useRef } from 'react';
import { workOrdersApi } from '@/api/workOrdersApi';
import { useSignalR } from '@/hooks/useSignalR';
import { useAuthStore } from '@/store/authStore';
import { useNotificationSound } from '@/hooks/useNotificationSound';
import { ChatThread, type ChatMessage, type TypingIndicator } from './ChatThread';

type TimeoutHandle = ReturnType<typeof setTimeout>;

interface FrontDeskChatPanelProps {
  workOrderId: number;
  vehicleInfo: string;
  roNumber: string;
  techName?: string | null;
  /** Called when an incoming message from someone else arrives while not focused */
  onNewMessage?: () => void;
}

const QUICK_REPLIES = [
  '👍 Looks good',
  '⏳ Running behind',
  '🔧 Need parts',
  '✅ Ready for pickup',
  '⚠️ Issue found',
  '💬 Questions?',
];

export function FrontDeskChatPanel({
  workOrderId,
  vehicleInfo,
  roNumber,
  techName,
  onNewMessage,
}: FrontDeskChatPanelProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [messageText, setMessageText] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(false);
  const [isTyping, setIsTyping] = useState<TypingIndicator | null>(null);

  const staffId = useAuthStore((s) => s.staffId);
  const staffName = useAuthStore((s) => s.staffName) ?? 'Front Desk';
  const seenIdsRef = useRef(new Set<number>());
  const onNewMessageRef = useRef(onNewMessage);
  onNewMessageRef.current = onNewMessage;
  const typingTimeoutRef = useRef<TimeoutHandle>();
  const typingEmitRef = useRef<TimeoutHandle>();
  const { soundEnabled, toggleSound, playNotificationSound } = useNotificationSound();

  const { on, off, invoke } = useSignalR();

  // Load history + mark all read on mount
  useEffect(() => {
    let mounted = true;
    const refresh = (initial = false) => {
      if (initial) setLoading(true);
      workOrdersApi.getWorkOrderChat(workOrderId).then((data: ChatMessage[]) => {
        if (!mounted) return;
        seenIdsRef.current = new Set(data.map((m) => m.chatMessageId));
        setMessages(data);
      }).catch(() => {}).finally(() => {
        if (mounted && initial) setLoading(false);
      });
    };
    refresh(true);
    const pollId = window.setInterval(() => refresh(false), 5_000);

    // Mark all existing messages as read
    workOrdersApi.markAllChatRead(workOrderId).catch(() => {});

    return () => { mounted = false; window.clearInterval(pollId); };
  }, [workOrderId]);

  // Real-time: listen for new messages on this work order
  useEffect(() => {
    const messageHandler = (...args: unknown[]) => {
      const receivedId = args[0] as number;
      const msg = args[1] as ChatMessage;
      if (receivedId !== workOrderId) return;
      if (seenIdsRef.current.has(msg.chatMessageId)) return;
      seenIdsRef.current.add(msg.chatMessageId);
      setMessages((prev) => [...prev, msg]);
      setIsTyping(null);
      // Notify parent if the message is from someone else
      if (msg.senderStaffId !== staffId) {
        onNewMessageRef.current?.();
        playNotificationSound();
      }
    };

    const typingHandler = (...args: unknown[]) => {
      const receivedId = args[0] as number;
      const typingData = args[1] as { senderName: string } | null;
      if (receivedId !== workOrderId || !typingData) return;
      setIsTyping({ senderName: typingData.senderName });
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => setIsTyping(null), 3000);
    };

    on('NewChatMessage', messageHandler);
    on('UserTyping', typingHandler);
    return () => {
      off('NewChatMessage', messageHandler);
      off('UserTyping', typingHandler);
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    };
  }, [workOrderId, staffId, on, off]);

  async function handleSend(e: React.FormEvent) {
    e.preventDefault();
    if (!messageText.trim()) return;
    setSending(true);
    try {
      const msg: ChatMessage = await workOrdersApi.sendWorkOrderChatMessage(workOrderId, {
        messageText: messageText.trim(),
      });
      if (!seenIdsRef.current.has(msg.chatMessageId)) {
        seenIdsRef.current.add(msg.chatMessageId);
        setMessages((prev) => [...prev, msg]);
      }
      setMessageText('');
    } catch {
      setSendError(true);
      setTimeout(() => setSendError(false), 4000);
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="flex flex-col h-full">
      {/* Thread header */}
      <div className="px-4 py-3 border-b border-slate-200 dark:border-border-dark flex-shrink-0 flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-900 dark:text-white truncate">{vehicleInfo}</p>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            RO #{roNumber}
            {techName ? ` · ${techName}` : ' · Unassigned'}
          </p>
        </div>
        <button
          onClick={() => toggleSound()}
          className={`p-1.5 rounded-lg transition-colors flex-shrink-0 ml-2 ${soundEnabled ? 'text-slate-900 dark:text-white hover:bg-slate-100 dark:hover:bg-slate-700' : 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'}`}
          title={soundEnabled ? 'Sound enabled' : 'Sound disabled'}
        >
          {soundEnabled ? (
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
            </svg>
          ) : (
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M16.6915026,12.4744748 L16.6915026,6.45258205 C16.6915026,4.51328067 15.1820742,2.99337217 13.2088889,2.99337217 C11.2356707,2.99337217 9.72624874,4.51328067 9.72624874,6.45258205 L9.72624874,12.4744748 C9.72624874,14.4136378 11.2356707,15.9335807 13.2088889,15.9335807 C15.1820742,15.9335807 16.6915026,14.4136378 16.6915026,12.4744748 Z M21.527211,12.4744748 C21.527211,12.4744748 21.527211,15.0151496 21.527211,15.4744748 L21.527211,20.0151496 C21.527211,21.6536872 20.1706324,23 18.518788,23 L7.49012012,23 C5.83827599,23 4.48179747,21.6536872 4.48179747,20.0151496 L4.48179747,15.4744748 L2.58923381,15.4744748 C1.31301674,15.4744748 0.317098571,14.4744748 0.317098571,13.1744748 C0.317098571,11.8744748 1.31301674,10.8744748 2.58923381,10.8744748 L4.48179747,10.8744748 L4.48179747,6.45258205 C4.48179747,2.86779827 7.39647773,0 11.0650301,0 C14.7335826,0 17.647268,2.86779827 17.647268,6.45258205 L17.647268,10.8744748 L19.5397317,10.8744748 C20.8159488,10.8744748 21.811864,11.8744748 21.811864,13.1744748 C21.811864,14.4744748 20.8159488,15.4744748 19.5397317,15.4744748 L17.647268,15.4744748 L17.647268,15.4744748 C17.647268,16.0425447 17.6467764,16.5425447 17.6467764,16.7325447 L17.6467764,20.0151496 C17.6467764,21.6536872 16.2901978,23 14.6384536,23 L7.49012012,23 C5.83827599,23 4.48179747,21.6536872 4.48179747,20.0151496 L4.48179747,16.7325447 C4.48179747,16.5425447 4.48135826,16.0425447 4.48135826,15.4744748 L4.48135826,15.4744748" />
            </svg>
          )}
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto min-h-0">
        <ChatThread
          messages={messages}
          currentStaffId={staffId}
          loading={loading}
          emptyText="No messages yet. Send the first message to the technician."
          isTyping={isTyping}
        />
      </div>

      {sendError && (
        <div className="px-3 py-1.5 bg-red-50 dark:bg-red-900/20 border-t border-red-200 dark:border-red-700 text-xs text-red-600 dark:text-red-400 flex-shrink-0">
          Failed to send message. Please try again.
        </div>
      )}

      {/* Input */}
      <form
        onSubmit={handleSend}
        className="flex gap-2 p-3 border-t border-slate-200 dark:border-border-dark flex-shrink-0"
      >
        <input
          type="text"
          value={messageText}
          onChange={(e) => {
            setMessageText(e.target.value);
            if (e.target.value.trim() && !sending) {
              if (!typingEmitRef.current) {
                invoke('NotifyTyping', workOrderId, staffName).catch(() => {});
              }
              clearTimeout(typingEmitRef.current);
              typingEmitRef.current = setTimeout(() => {
                typingEmitRef.current = undefined;
              }, 2000);
            }
          }}
          placeholder="Message technician…"
          disabled={sending}
          className="flex-1 px-3 py-2 border border-slate-300 dark:border-border-dark bg-white dark:bg-surface-dark rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-600 disabled:opacity-50"
        />
        <button
          type="submit"
          disabled={!messageText.trim() || sending}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-semibold hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          {sending ? '…' : 'Send'}
        </button>
      </form>

      {/* Quick replies — show if input is empty */}
      {!messageText.trim() && (
        <div className="px-3 py-2 bg-slate-50 dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700 flex flex-wrap gap-2">
          {QUICK_REPLIES.map((reply) => (
            <button
              key={reply}
              onClick={() => {
                setMessageText(reply);
              }}
              className="text-xs px-2 py-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors text-slate-700 dark:text-slate-300 whitespace-nowrap"
            >
              {reply}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
